// Oude matras retour — cart toggle. Lets customers add the retour service to a product straight
// from the cart (page + drawer) by choosing a size (Eenpersoons/Tweepersoons), without going back
// to the product page. Mirror of assets/montage-cart-toggle.js, but click-driven via two size
// buttons instead of a single switch.
//
// On a size click it:
//   1. reads the live parent line (to keep its existing properties + quantity),
//   2. tags the parent with a hidden _retour_pair_id via /cart/change.js (properties are replaced
//      wholesale, so we merge the existing ones back in),
//   3. adds the chosen retour variant as a coupled addon line via /cart/add.js (carrying the same
//      _retour_pair_id + _is_retour_addon marker),
//   4. re-renders the cart section(s), after which cart-products.liquid draws the paired "+" line
//      and this toggle disappears.
//
// Removing the retour line again uses the "Verwijderen" link on the paired row; the toggle reappears.
import { CartUpdateEvent } from "@theme/events";

const PAIR_KEY = "_retour_pair_id";
const ADDON_KEY = "_is_retour_addon";

function getRoutes() {
  const r = (window.MorgennTheme && window.MorgennTheme.routes) || {};
  return {
    cart: r.cart || "/cart.js",
    cartAdd: r.cart_add_url || "/cart/add.js",
    cartChange: r.cart_change_url || "/cart/change.js",
  };
}

function collectSectionIds() {
  const ids = new Set();
  document
    .querySelectorAll("cart-items-component[data-section-id], cart-drawer-component[data-section-id]")
    .forEach((el) => {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
  return Array.from(ids);
}

function generatePairId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

class RetourCartToggleComponent extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.buttons = Array.from(this.querySelectorAll("button[data-variant-id]"));
    this._onClick = this.onClick.bind(this);
    this.buttons.forEach((btn) => btn.addEventListener("click", this._onClick));
    if (this.checkbox) {
      this._onToggle = this.onToggle.bind(this);
      this.checkbox.addEventListener("change", this._onToggle);
    }
  }

  disconnectedCallback() {
    if (this._onClick) {
      this.buttons.forEach((btn) => btn.removeEventListener("click", this._onClick));
    }
    if (this.checkbox && this._onToggle) {
      this.checkbox.removeEventListener("change", this._onToggle);
    }
  }

  // The switch reveals the two size buttons; picking a size adds the coupled retour line.
  onToggle() {
    this.classList.toggle("is-open", this.checkbox.checked);
  }

  async onClick(event) {
    const variantId = event.currentTarget && event.currentTarget.dataset.variantId;
    if (!variantId) return;
    if (this._busy) return;
    this._busy = true;
    this.classList.add("is-loading");

    const parentKey = this.dataset.parentKey;
    if (!parentKey) {
      this.#reset();
      return;
    }

    const routes = getRoutes();
    const sectionIds = collectSectionIds();
    const sectionsParam = sectionIds.length > 0 ? sectionIds.join(",") : null;

    try {
      // 1. Read the live parent line so we keep its existing properties + quantity.
      const cartRes = await fetch(routes.cart, { headers: { Accept: "application/json" } });
      const cart = cartRes.ok ? await cartRes.json() : null;
      const parent = cart && cart.items && cart.items.find((it) => it.key === parentKey);
      if (!parent) {
        this.#reset();
        return;
      }

      const pairId = generatePairId();
      const quantity = parent.quantity || 1;

      // 2. Tag the parent with the coupling id (properties are replaced, so merge existing back in).
      const mergedProps = Object.assign({}, parent.properties || {});
      mergedProps[PAIR_KEY] = pairId;

      const changeBody = { id: parentKey, quantity, properties: mergedProps };
      if (sectionsParam) changeBody.sections = sectionsParam;

      const changeRes = await fetch(routes.cartChange, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(changeBody),
      });
      if (!changeRes.ok) throw new Error("cart/change failed: " + changeRes.status);

      // 3. Add the chosen retour variant as a coupled addon line (matching quantity).
      const fd = new FormData();
      fd.append("id", variantId);
      fd.append("quantity", String(quantity));
      fd.append(`properties[${PAIR_KEY}]`, pairId);
      fd.append(`properties[${ADDON_KEY}]`, "1");
      if (sectionsParam) fd.append("sections", sectionsParam);

      const addRes = await fetch(routes.cartAdd, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      });
      if (!addRes.ok) throw new Error("cart/add failed: " + addRes.status);
      const addData = await addRes.json();

      // 4. Re-render the cart section(s) with the new paired line (absolute item count).
      const freshRes = await fetch(routes.cart, { headers: { Accept: "application/json" } });
      const freshCart = freshRes.ok ? await freshRes.json() : {};
      document.dispatchEvent(
        new CartUpdateEvent(freshCart, this.id || "retour-cart-toggle", {
          itemCount: freshCart.item_count,
          source: "retour-cart-toggle",
          variantId: variantId,
          sections: addData.sections,
        })
      );
    } catch (err) {
      console.error("[retour-cart-toggle]", err);
      this.#reset();
    }
  }

  #reset() {
    this._busy = false;
    this.classList.remove("is-loading");
  }
}

if (!customElements.get("retour-cart-toggle-component")) {
  customElements.define("retour-cart-toggle-component", RetourCartToggleComponent);
}
